import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Empty, Flex, Form, Input, InputNumber, Modal, Select, Table, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useI18n } from "@/i18n";
import { ConditionTag, type Condition } from "../inventory/conditions";

type Bin = { key: string; skuId: string; skuCode: string; skuName: string; lotId: string | null; lotNo: string | null; condition: Condition; free: number };

/**
 * "Tugas pindah": pick a bin, say how many of each of its units go and
 * where; staff then move them by scanning, like a putaway (Plan §6.8).
 */
export function MoveTaskModal({ open, warehouseId, onClose }: { open: boolean; warehouseId: string; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();
  const [qty, setQty] = useState<Record<string, number | null>>({});
  const [note, setNote] = useState("");

  const locs = useQuery({
    queryKey: ["locations", warehouseId, "all"],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/locations", { params: { path: { warehouseID: warehouseId }, query: { status: "active" } } })),
    enabled: open,
  });
  const usable = (locs.data?.items ?? []).filter((l) => l.kind !== "variance" && !l.countId);
  const stock = useQuery({
    queryKey: ["locations", "stock", from],
    queryFn: () => unwrap(api.GET("/v1/inventory/locations/{locationID}", { params: { path: { locationID: from! } } })),
    enabled: open && !!from,
  });
  const bins: Bin[] = (stock.data?.items ?? [])
    .map((b) => ({ key: `${b.skuId}|${b.lotId ?? ""}|${b.condition}`, skuId: b.skuId, skuCode: b.skuCode, skuName: b.skuName, lotId: b.lotId, lotNo: b.lotNo, condition: b.condition, free: b.quantity - b.reserved }))
    .filter((b) => b.free > 0);

  const reset = () => {
    setFrom(undefined);
    setTo(undefined);
    setQty({});
    setNote("");
  };
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/warehouses/{warehouseID}/moves", {
          params: { path: { warehouseID: warehouseId } },
          body: {
            note: note.trim(),
            lines: bins
              .filter((b) => (qty[b.key] ?? 0) > 0)
              .map((b) => ({ skuId: b.skuId, fromLocationId: from!, lotId: b.lotId, condition: b.condition, quantity: qty[b.key]!, toLocationId: to ?? null })),
          },
        }),
      ),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
      reset();
      onClose();
      navigate(`/app/warehouse/tasks/${r.taskId}`);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const any = bins.some((b) => (qty[b.key] ?? 0) > 0);
  const options = usable.map((l) => ({ value: l.id, label: l.code }));
  return (
    <Modal
      open={open}
      width={760}
      title={t("wms.move.title")}
      okText={t("wms.move.create")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: !any }}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={() => {
        reset();
        onClose();
      }}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("wms.move.help")}</Typography.Paragraph>
      <Flex gap={12} wrap>
        <Form.Item label={t("wms.move.from")} style={{ flex: 1, minWidth: 200 }}>
          <Select
            showSearch={{ optionFilterProp: "label" }}
            loading={locs.isPending}
            value={from}
            onChange={(v) => {
              setFrom(v);
              setQty({});
            }}
            options={options}
          />
        </Form.Item>
        <Form.Item label={t("wms.move.to")} extra={t("wms.move.toHelp")} style={{ flex: 1, minWidth: 200 }}>
          <Select showSearch={{ optionFilterProp: "label" }} allowClear value={to} onChange={setTo} options={options.filter((o) => o.value !== from)} placeholder={t("wms.move.suggested")} />
        </Form.Item>
      </Flex>
      {from &&
        (bins.length === 0 && !stock.isPending ? (
          <Empty description={t("wms.move.empty")} />
        ) : (
          <Table<Bin>
            size="small"
            rowKey="key"
            loading={stock.isPending}
            dataSource={bins}
            pagination={false}
            columns={[
              {
                title: t("catalog.code"),
                dataIndex: "skuCode",
                render: (v: string, b) => (
                  <Flex vertical>
                    <Typography.Text strong className="z-num">
                      {v}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {b.skuName}
                      {b.lotNo ? ` · ${t("inventory.bin.lot")} ${b.lotNo}` : ""}
                    </Typography.Text>
                  </Flex>
                ),
              },
              { title: t("inventory.condition"), dataIndex: "condition", width: 110, render: (v: Condition) => <ConditionTag condition={v} /> },
              { title: t("wms.move.free"), dataIndex: "free", width: 80, align: "right", render: (v: number) => formatNumber(v) },
              {
                title: t("wms.move.qty"),
                width: 150,
                render: (_, b) => (
                  <InputNumber min={0} max={b.free} precision={0} value={qty[b.key] ?? null} onChange={(v) => setQty((q) => ({ ...q, [b.key]: v }))} style={{ width: "100%" }} />
                ),
              },
            ]}
          />
        ))}
      <Input.TextArea rows={2} maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("wms.in.note")} style={{ marginTop: 12 }} />
    </Modal>
  );
}
